/**
 * Saying which half of a board is real.
 *
 * The shell already warns when the whole session is sample data. The case it
 * did not cover is the one that now exists: the platform is answering, so no
 * banner shows, and yet a board still draws friction runs, rubber loading, the
 * facilities register and fifty live feeds from data that was written for a
 * demonstration. An operator reading a real work order count on the same screen
 * has every reason to believe the rest of it.
 *
 * So on the boards, and only there, the split is stated. It is deliberately one
 * sentence naming both halves rather than a per-screen judgement: most boards
 * draw from both, and a badge saying REAL or SAMPLE over a screen that is
 * partly each would be worse than none.
 */
import { useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import s from "../../layout/AppShell.module.css";
import { t } from "../../i18n";
/* only the counts, never the merge: reading them from `merge.ts` would put the
   demo's reference tables in the entry chunk — see report.ts */
import { feedReport, subscribeFeed } from "./report";
import { safetyFeedReport, subscribeSafetyFeed, type SafetyFeedReport } from "./feeds/safetyReport";

/* The safety boards, whose registers come from the platform (build 2). */
const SAFETY_BOARDS = new Set(["sms", "safety"]);

function SafetyLine({ r }: { r: SafetyFeedReport }) {
  const counted = r.hazards != null || r.reports != null || r.incidents != null;
  return (
    <div className={s.banner} role="status" data-testid="safety-notice">
      <span className={s.bannerTag}>{t("FROM THE PLATFORM")}</span>
      <span>
        {counted
          ? t("{hz} hazards, {sr} safety reports and {inc} incidents on this board are from the platform.", {
              hz: r.hazards ?? 0,
              sr: r.reports ?? 0,
              inc: r.incidents ?? 0,
            })
          : t("The safety registers on this board are read from the platform.")}
        {r.refused.length > 0 && ` ${t("Not read for your role: {why}.", { why: r.refused.join(" · ") })}`}
      </span>
    </div>
  );
}

import { frictionReport, subscribeFrictionReport, type FrictionReport } from "./frictionReport";

/** On /friction and /rubber: what is the airport's own, and what is built into the screen. */
function FrictionNotice({ r }: { r: FrictionReport }) {
  return (
    <div className={s.banner} role="status" data-testid="friction-notice">
      <span className={s.bannerTag}>{t("PART SAMPLE")}</span>
      <span>
        {t(
          "{s} surveys, {tests} friction tests, {rm} rubber removals and {rt} filed returns on this board are from the platform. The July survey report and the surface condition are built into this screen.",
          { s: r.surveys, tests: r.tests, rm: r.removals, rt: r.returns },
        )}
        {r.refused.length > 0 && ` ${t("Some records could not be read: {why}", { why: r.refused.join("; ") })}`}
      </span>
    </div>
  );
}

/* The routes that render a ported board, derived from the router's own lists
   (build 7 — the hand-kept list here missed every dashboard board). */
import { isBoardRoute } from "../boards/boardRoutes";

export function FeedNotice() {
  const { source } = useSession();
  const { pathname } = useLocation();
  /* the merge happens inside a lazily loaded chunk the shell has no other
     connection to, so the counts are subscribed to rather than read once —
     and subscribed to before the early returns, because a hook may not be
     called conditionally */
  const r = useSyncExternalStore(subscribeFeed, feedReport, feedReport);
  const safety = useSyncExternalStore(subscribeSafetyFeed, safetyFeedReport, safetyFeedReport);
  const fr = useSyncExternalStore(subscribeFrictionReport, frictionReport, frictionReport);

  /* the shell's own SAMPLE DATA banner already covers the mock adapter */
  if (source.kind !== "platform") return null;
  if (SAFETY_BOARDS.has(pathname.replace(/^\/+|\/+$/g, ""))) return <SafetyLine r={safety} />;
  if (!isBoardRoute(pathname)) return null;
  /* the friction and rubber boards read the airport's own record now */
  if (fr.live && /^\/*(friction|rubber)\/*$/.test(pathname)) return <FrictionNotice r={fr} />;
  /* build 7: rounds, utility checks, assessments and breakdowns are the platform's now; say which half is sample */
  return (
    <div className={s.banner} role="status" data-testid="part-sample">
      <span className={s.bannerTag}>{t("PART SAMPLE")}</span>
      <span>
        {r.workOrders.unlocated > 0
          ? t(
              "{wo} work orders, {ins} inspections and {docs} documents here are from the platform. {n} of the work orders have no position, so they are listed but not drawn. The survey, surface reports, the facilities and carousel registers, projects and live feeds are sample data, not real.",
              { wo: r.workOrders.fed, ins: r.inspections.fed, docs: r.documents.fed, n: r.workOrders.unlocated },
            )
          : t(
              "{wo} work orders, {ins} inspections and {docs} documents here are from the platform. The survey, surface reports, the facilities and carousel registers, projects and live feeds are sample data, not real.",
              { wo: r.workOrders.fed, ins: r.inspections.fed, docs: r.documents.fed },
            )}
      </span>
    </div>
  );
}
